import type { OperationVariables, WatchQueryOptions } from '@apollo/client';
import React from 'react';
import Button from '~/components/atom/Button';
import Icon from '~/components/atom/Icon';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body } from '~/components/atom/Typography';
import {
  useUpdateRealworksAppStatusMutation,
  type Exact,
  type GetAppStatusRealworksQuery,
  type RealworksRelatieSyncType,
} from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import formatToastMessage from '~/util/formatToastMessage';

const text = {
  noResults:
    'Geen Realworks kenmerken gevonden voor de actieve relatie token. Voeg een andere token toe of probeer het later opnieuw.',
  buttons: {
    activate: 'Activeer synchronisatie',
    deactivate: 'Deactiveer synchronisatie',
  },
  toasts: {
    error: {
      sync: 'Er is iets fout gegaan bij het wijzigen van de synchronisatie, probeer het later opnieuw',
    },
    success: {
      deactivate: 'Synchronisatie gedeactiveerd',
      activate: 'Synchronisatie geactiveerd',
    },
  },
};

export type Props = {
  dataTestId?: string;
  className?: string;
  isSyncEnabled: boolean;
  updateQuery: <
    TVars extends OperationVariables = Exact<{ accountId: string }>,
  >(
    mapFn: (
      previousQueryResult: GetAppStatusRealworksQuery,
      options: Pick<
        WatchQueryOptions<TVars, GetAppStatusRealworksQuery>,
        'variables'
      >,
    ) => GetAppStatusRealworksQuery,
  ) => void;
  syncType: RealworksRelatieSyncType | null | undefined;
};

const NoKenmerken: React.FCC<Props> = ({
  isSyncEnabled,
  updateQuery,
  syncType,
}) => {
  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();

  const [updateRealworksAppStatus, { loading: updating }] =
    useUpdateRealworksAppStatusMutation();

  const updateSyncWithNoKenmerken = () =>
    updateRealworksAppStatus({
      variables: {
        accountId,
        updateRelatieSync: {
          enabled: !isSyncEnabled,
          type: syncType,
          kenmerkMapping: [],
        },
      },
    }).then(({ errors, data }) => {
      if ((errors && errors.length > 0) || !data) {
        addToast([formatToastMessage(text.toasts.error.sync, 'danger')]);
        return;
      }

      updateQuery(prevRes => ({
        ...prevRes,
        getAppStatusRealworks: data.updateRealworksAppStatus,
      }));

      addToast([
        formatToastMessage(
          isSyncEnabled
            ? text.toasts.success.deactivate
            : text.toasts.success.activate,
          'success',
        ),
      ]);
    });

  return (
    <JustificationContainer
      direction="column"
      gap="l"
      align="center"
      padding={['xxxl', 'l', null, 'l']}
    >
      <JustificationContainer direction="column" gap="base" align="center">
        <Icon
          size="l"
          name="tag"
          color={{ group: 'primary', variant: 'light' }}
          strokeWidth={1.2}
        />
        <Body color={{ group: 'primary', variant: 'light' }} align="center">
          {text.noResults}
        </Body>
      </JustificationContainer>
      <Button
        label={isSyncEnabled ? text.buttons.deactivate : text.buttons.activate}
        onClick={updateSyncWithNoKenmerken}
        appearance={isSyncEnabled ? 'danger' : 'secondary'}
        loading={updating}
      />
    </JustificationContainer>
  );
};

export default NoKenmerken;
